import React, { useEffect, useState } from 'react';

// Hooks
import { useDebounce } from '../../hooks/useDebounce';
import { useTranslation } from 'react-i18next';

// Components
import Modal from '../Modal/Modal';
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import { Screen } from '../../store/models/screens';
import { SEARCH_TEXT_LENGTH_LIMITS } from '../../constants/common';

type SearchScreen = {
  name?: string
}

type ComponentProps = {
  visibility: boolean;
  onClose: () => void;
  getScreenByContractId: ((payload: {
    query?: SearchScreen;
  }) => Promise<Screen[]>);
  screensBySearch: Screen[];
  attachScreen: (id: string) => Promise<void>;
  showTransferOption?: boolean;
  currentScreenId?: string;
  transferScreen?: (newScreenId: string, oldScreenId: string) => Promise<void>;
};

const SearchScreen: React.FC<ComponentProps> = ({
  visibility,
  onClose,
  getScreenByContractId,
  screensBySearch,
  attachScreen,
  showTransferOption = false,
  currentScreenId,
  transferScreen,
}) => {
  const { t } = useTranslation();
  const [allScreens, setAllScreens] = useState<Screen[] | null>(null);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const debouncedSearchQuery = useDebounce<string>(searchQuery, 300);

  const updateSearchText = (event) => {
    const { value } = event.currentTarget;
    setSearchQuery(value);
  };

  const addCycle = async (screen: Screen) => {
    await onClose();
    await attachScreen(screen.id);
  };

  const transferCycle = async (screen: Screen) => {
    if (transferScreen && currentScreenId) {
      await onClose();
      await transferScreen(screen.id, currentScreenId);
    }
  };

  const fetchAll = async () => {
    const response = await getScreenByContractId({});
    setAllScreens(response);
  };

  const searchCyclesByName = async (name: string) => {
    const query: SearchScreen = { name };
    await getScreenByContractId({ query });
  };

  const getListCycles = (query: string) => {
    let screens = query && query.length >= SEARCH_TEXT_LENGTH_LIMITS.MIN ? screensBySearch : allScreens;
    
    // Filter out current screen when in transfer mode
    if (showTransferOption && currentScreenId) {
      screens = screens?.filter(screen => screen.id !== currentScreenId) || [];
    }
    
    return screens;
  }

  useEffect(() => {
    fetchAll()
  }, []);

  useEffect(() => {
    if (debouncedSearchQuery && debouncedSearchQuery?.length >= SEARCH_TEXT_LENGTH_LIMITS.MIN) {
      searchCyclesByName(debouncedSearchQuery);
    }
    else {
      fetchAll();
    }
  }, [debouncedSearchQuery]);

  return (
    <Modal
      isVisible={visibility}
      onClose={onClose}
      modalTitle='Choose screens'
      showAcceptBtn={false}
      showCancelBtn={false}
    >
      <div className="c-cycle-search-screen h-full w-full">
        {allScreens === null ? (
          <div className='h-75 w-full flex justify-center items-center'><SpinnerLoader /></div>
        ) : (
          (
            <div className='h-80 flex flex-col'>
              <div className='pb-3'>
                <InputControl
                  type="text"
                  name="email"
                  placeholder="Enter screen name"
                  value={searchQuery || ''}
                  onChange={updateSearchText}
                  autoComplete="off"
                />
              </div>
              <div className='c-cycle-search-screen__wrapper-list overflow-y-auto flex-auto mb-7'>
                {screensBySearch.length !== 0 && (
                  <ul className="c-cycle-search-screen__list flex-auto flex flex-col gap-y-2 overflow-y-auto overflow-x-hidden">
                    {getListCycles(searchQuery).map((cycle) => (
                      <li key={cycle.id} className='grid gap-x-2 w-full items-center grid-cols-[1fr,auto]'>
                        <span className='truncate text-left'>{cycle.name}</span>
                        <div className='flex gap-x-2'>
                          {showTransferOption ? (
                            <InteractiveButton
                              text={t('commonText.transferToScreen')}
                              onClick={() => transferCycle(cycle)}
                              className='text-xs px-2 py-1'
                            />
                          ) : (
                            <InteractiveButton
                              text='Add screen'
                              onClick={() => addCycle(cycle)}
                              className='w-31'
                            />
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {screensBySearch.length === 0 && searchQuery && searchQuery.length >= SEARCH_TEXT_LENGTH_LIMITS.MIN && (
                  <div className='flex-auto flex justify-center items-center'>
                    <span>No one screen found</span>
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </div>
    </Modal>
  );
};

export default SearchScreen;
